import { useCallback } from 'react'
import { useFileStore } from '../stores/fileStore'
import { useEditorStore } from '../stores/editorStore'
import { fileStorage, downloadFile, readFileAsText, isMarkdownFile } from '../utils/file'
import { createNewFile, renderMarkdown } from '../utils/markdown'

export function useFileSystem() {
  const { files, activeFileId, addFile, removeFile, updateFile, setActiveFile } = useFileStore()
  const { setContent, content } = useEditorStore()

  const saveCurrentFile = useCallback(async () => {
    if (!activeFileId) return
    try {
      await fileStorage.saveFile(activeFileId, content)
      updateFile(activeFileId, { content })
      useEditorStore.getState().setIsModified(false)
    } catch (error) {
      console.error('Failed to save file:', error)
    }
  }, [activeFileId, content, updateFile])

  const openFile = useCallback(
    async (id: string) => {
      try {
        const data = await fileStorage.loadFile(id)
        if (data) {
          setContent(data.content)
          setActiveFile(id)
        }
      } catch (error) {
        console.error('Failed to open file:', error)
      }
    },
    [setContent, setActiveFile]
  )

  const createFile = useCallback(
    (name: string = 'Untitled.md') => {
      const file = createNewFile(name)
      addFile(file)
      setActiveFile(file.id)
      setContent('')
      return file
    },
    [addFile, setActiveFile, setContent]
  )

  const deleteFileById = useCallback(
    async (id: string) => {
      try {
        await fileStorage.deleteFile(id)
        removeFile(id)
      } catch (error) {
        console.error('Failed to delete file:', error)
      }
    },
    [removeFile]
  )

  const importFile = useCallback(async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.md,.markdown,.txt'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      const text = await readFileAsText(file)
      const name = file.name
      const newFile = createNewFile(name)
      newFile.content = text
      addFile(newFile)
      setActiveFile(newFile.id)
      setContent(text)
      await fileStorage.saveFile(newFile.id, text)
    }
    input.click()
  }, [addFile, setActiveFile, setContent, createNewFile])

  const exportCurrentFile = useCallback(
    (format: 'markdown' | 'html' | 'txt' = 'markdown') => {
      const activeFile = files.find((f) => f.id === activeFileId)
      const filename = activeFile?.name || 'document.md'

      switch (format) {
        case 'markdown':
          downloadFile(content, filename, 'text/markdown')
          break
        case 'html': {
          const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${filename}</title><style>body{max-width:800px;margin:0 auto;padding:20px;font-family:sans-serif;}</style></head><body>${renderMarkdown(content)}</body></html>`
          downloadFile(html, filename.replace(/\.md$/, '.html'), 'text/html')
          break
        }
        case 'txt':
          downloadFile(content, filename.replace(/\.md$/, '.txt'), 'text/plain')
          break
      }
    },
    [content, files, activeFileId]
  )

  return {
    saveCurrentFile,
    openFile,
    createFile,
    deleteFileById,
    importFile,
    exportCurrentFile,
  }
}
